import { CheckCircle, ContentCopy } from '@mui/icons-material';
import { Alert, Box, Card, Chip, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

const PERSON_TYPE = {
    FISICA: 'Pessoa física',
    JURIDICA: 'Pessoa jurídica',
};

const COMPANY_TYPE = {
    MEI: 'MEI',
    LIMITED: 'LTDA',
    INDIVIDUAL: 'Individual',
    ASSOCIATION: 'Associação',
};

const STATUS_LABEL = {
    APPROVED: 'Aprovado',
    AWAITING_APPROVAL: 'Aguardando aprovação',
    PENDING: 'Pendente',
    REJECTED: 'Reprovado',
};

function Field({ label, value, mono, hint }) {
    if (!value) return null;
    return (
        <Box minWidth={180}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined} sx={{ wordBreak: 'break-all' }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

function statusChip(status) {
    if (!status) return null;
    const label = STATUS_LABEL[status] || status;
    const color = status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'error' : 'warning';
    return <Chip size="small" variant="outlined" color={color} label={label} />;
}

export default function AsaasAccountCard({ data, loading, error, webhooks = [] }) {
    const [copied, setCopied] = useState(false);
    const webhookUrl = data?.webhookUrl || '';
    const asaasBaseUrl = data?.asaasBaseUrl || '';

    const matched = useMemo(() => {
        if (!webhookUrl) return false;
        const normalize = value => String(value || '').replace(/\/$/, '').toLowerCase();
        const expected = normalize(webhookUrl);
        return webhooks.some(item => normalize(item?.url) === expected);
    }, [webhookUrl, webhooks]);

    const environmentLabel =
        data?.environment === 'sandbox' ? 'Sandbox' : data?.environment === 'production' ? 'Produção' : data?.environment;

    const account = data?.account;
    const generalStatus = data?.accountStatus?.general || data?.accountStatus?.commercialInfo || account?.status;

    const copyUrl = async () => {
        if (!webhookUrl) return;
        await navigator.clipboard.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <Stack spacing={2} p={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Conta Asaas do admin
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Mesmo cliente de propostas, planos e contratos:{' '}
                            {data?.client || 'server-eurobem/src/service/Asaas.js'}
                        </Typography>
                    </Box>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {environmentLabel && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color={data?.environment === 'sandbox' ? 'warning' : 'primary'}
                                label={environmentLabel}
                            />
                        )}
                        <Chip
                            size="small"
                            variant="outlined"
                            color={data?.hasApiKey ? 'success' : 'warning'}
                            label={data?.asaasKeySuffix ? `ASAAS_KEY ${data.asaasKeySuffix}` : 'ASAAS_KEY ausente'}
                        />
                        {statusChip(generalStatus)}
                        {matched ? (
                            <Chip size="small" color="success" icon={<CheckCircle />} label="Webhook desta API cadastrado" />
                        ) : (
                            webhookUrl && (
                                <Chip size="small" variant="outlined" label="Webhook desta API ainda não cadastrado" />
                            )
                        )}
                    </Stack>
                </Stack>

                {loading && !data ? (
                    <Stack spacing={1}>
                        <Skeleton height={28} />
                        <Skeleton height={28} />
                    </Stack>
                ) : null}

                {(error || data?.error) && (
                    <Alert severity="warning">
                        {error?.response?.data?.message || data?.error || 'Não foi possível consultar a conta Asaas.'}
                    </Alert>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} gap={3} flexWrap="wrap">
                    <Field
                        label="ASAAS_BASE_URL"
                        value={asaasBaseUrl || 'ausente no process.env deste server-eurobem'}
                        mono
                        hint="Lida agora do processo que o admin chama (VITE_SERVER). Chip Sandbox = esta URL contém “sandbox”."
                    />

                    <Box flex={1} minWidth={240}>
                        <Typography variant="caption" color="text.secondary">
                            Webhook desta API
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                                {webhookUrl || 'Defina API_BASE_URL com a origem pública do server-eurobem'}
                            </Typography>
                            {webhookUrl ? (
                                <IconButton size="small" onClick={copyUrl} title={copied ? 'Copiado' : 'Copiar'}>
                                    {copied ? <CheckCircle fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                                </IconButton>
                            ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            POST /webhooks/asaas neste server-eurobem. Não é webhook de clube.
                        </Typography>
                    </Box>
                </Stack>

                {account ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={3} flexWrap="wrap">
                        <Field label="Nome" value={account.companyName || account.name} />
                        <Field label="Documento" value={account.cpfCnpj} mono />
                        <Field label="E-mail" value={account.email} />
                        <Field
                            label="Tipo"
                            value={[
                                PERSON_TYPE[account.personType] || account.personType,
                                COMPANY_TYPE[account.companyType] || account.companyType,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        />
                        <Field label="Telefone" value={account.phone} />
                    </Stack>
                ) : null}
            </Stack>
        </Card>
    );
}
